import { apiClient } from '@/shared/api/client'
import type { ApiCollection, ApiPaginatedCollection, ApiResource } from '@/shared/types/api'
import type {
  AgentSlot,
  CatalogSyncRun,
  ConsentAuditRow,
  ConsentVersion,
  Conversation,
  KbSuggestion,
  KnowledgeDocument,
  KnowledgeSource,
  KnowledgeStatus,
  Message,
  OrganizationInvitation,
  Product,
  Project,
  ProjectMember,
  ProjectPreset,
  ProjectTag,
  Prompt,
  WidgetMobileLayout,
} from '@/shared/types/domain'

export interface UpsertProjectInput {
  name: string
  description?: string
  ai_provider?: string | null
  ai_model?: string | null
  knowledge_provider?: string | null
  knowledge_model?: string | null
  system_prompt?: string | null
  widget_enabled?: boolean
  widget_allowed_domains?: string[]
  widget_launcher_label?: string | null
  widget_launcher_show_label?: boolean
  widget_launcher_size?: 'small' | 'medium' | 'large'
  widget_launcher_bg_color?: string | null
  widget_launcher_icon_color?: string | null
  widget_launcher_text_color?: string | null
  widget_mobile_layout?: WidgetMobileLayout
  widget_position?: string | null
  widget_accent_color?: string | null
  telegram_enabled?: boolean
  telegram_supergroup_id?: string | null
  allow_handoff?: boolean
}

export interface UpsertPromptInput {
  title: string
  content: string
  is_active?: boolean
  sort?: number
  applicable_agents?: AgentSlot[] | null
}

export interface AddProjectMemberInput {
  user_id: number | string
}

export interface ListProjectsParams {
  archived?: 'only' | 'with'
}

export async function listProjects(params: ListProjectsParams = {}): Promise<Project[]> {
  const response = await apiClient.get<ApiCollection<Project>>('/projects', { params })
  return response.data.data
}

export async function archiveProject(
  projectId: string,
  options: { suppressErrorToast?: boolean } = {},
): Promise<Project> {
  const response = await apiClient.post<ApiResource<Project>>(
    `/projects/${projectId}/archive`,
    undefined,
    { suppressErrorToast: options.suppressErrorToast },
  )
  return response.data.data
}

export async function unarchiveProject(projectId: string): Promise<Project> {
  const response = await apiClient.delete<ApiResource<Project>>(`/projects/${projectId}/archive`)
  return response.data.data
}

export async function listProjectTags(): Promise<ProjectTag[]> {
  const response = await apiClient.get<ApiCollection<ProjectTag>>('/project-tags')
  return response.data.data
}

export async function attachProjectTag(
  projectId: string,
  payload: { name: string } | { tag_id: string },
): Promise<ProjectTag[]> {
  const response = await apiClient.post<ApiCollection<ProjectTag>>(
    `/projects/${projectId}/tags`,
    payload,
  )
  return response.data.data
}

export async function detachProjectTag(projectId: string, tagId: string): Promise<ProjectTag[]> {
  const response = await apiClient.delete<ApiCollection<ProjectTag>>(
    `/projects/${projectId}/tags/${tagId}`,
  )
  return response.data.data
}

export async function getProject(projectId: string): Promise<Project> {
  const response = await apiClient.get<ApiResource<Project>>(`/projects/${projectId}`)
  return response.data.data
}

export async function createProject(input: UpsertProjectInput): Promise<Project> {
  const response = await apiClient.post<ApiResource<Project>>('/projects', input)
  return response.data.data
}

export async function updateProject(
  projectId: string,
  input: UpsertProjectInput,
): Promise<Project> {
  const response = await apiClient.put<ApiResource<Project>>(`/projects/${projectId}`, input)
  return response.data.data
}

export async function deleteProject(projectId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}`)
}

export async function uploadWidgetIcon(
  projectId: string,
  file: File,
): Promise<{ icon_url: string }> {
  const formData = new FormData()
  formData.append('icon', file)
  const response = await apiClient.post<ApiResource<{ icon_url: string }>>(
    `/projects/${projectId}/widget-icon`,
    formData,
  )
  return response.data.data
}

export async function deleteWidgetIcon(projectId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/widget-icon`)
}

export async function listPrompts(projectId: string): Promise<Prompt[]> {
  const response = await apiClient.get<ApiCollection<Prompt>>(`/projects/${projectId}/prompts`)
  return response.data.data
}

export async function createPrompt(projectId: string, input: UpsertPromptInput): Promise<Prompt> {
  const response = await apiClient.post<ApiResource<Prompt>>(`/projects/${projectId}/prompts`, {
    title: input.title,
    content: input.content,
    is_active: input.is_active ?? false,
    sort: input.sort,
    applicable_agents: input.applicable_agents ?? null,
  })

  return response.data.data
}

export async function updatePrompt(
  projectId: string,
  promptId: string,
  input: UpsertPromptInput,
): Promise<Prompt> {
  const response = await apiClient.put<ApiResource<Prompt>>(
    `/projects/${projectId}/prompts/${promptId}`,
    {
      title: input.title,
      content: input.content,
      is_active: input.is_active ?? false,
      sort: input.sort,
      applicable_agents: input.applicable_agents ?? null,
    },
  )

  return response.data.data
}

export async function deletePrompt(projectId: string, promptId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/prompts/${promptId}`)
}

export async function reorderPrompts(projectId: string, ids: string[]): Promise<Prompt[]> {
  const response = await apiClient.patch<ApiCollection<Prompt>>(
    `/projects/${projectId}/prompts/reorder`,
    { ids },
  )
  return response.data.data
}

export async function listConversations(projectId: string): Promise<Conversation[]> {
  const response = await apiClient.get<ApiCollection<Conversation>>(
    `/projects/${projectId}/conversations`,
  )
  return response.data.data
}

export interface OperatorConversationFilters {
  status?: string
  intercepted?: boolean
  needs_reply?: boolean
  project_id?: string
}

export async function listOperatorConversations(
  filters: OperatorConversationFilters = {},
): Promise<Conversation[]> {
  const response = await apiClient.get<ApiCollection<Conversation>>('/operator/conversations', {
    params: filters,
  })
  return response.data.data
}

export async function countOperatorConversations(): Promise<number> {
  const response = await apiClient.get<{ data: { count: number } }>('/operator/conversations/count')
  return response.data.data.count
}

export async function createConversation(projectId: string, title?: string): Promise<Conversation> {
  const response = await apiClient.post<ApiResource<Conversation>>(
    `/projects/${projectId}/conversations`,
    {
      title,
    },
  )
  return response.data.data
}

export async function getConversation(
  projectId: string,
  conversationId: string,
): Promise<Conversation> {
  const response = await apiClient.get<ApiResource<Conversation>>(
    `/projects/${projectId}/conversations/${conversationId}`,
  )
  return response.data.data
}

export interface UnreadConversationsSummary {
  counts: Record<string, number>
  total: number
  leads?: {
    counts: Record<string, number>
    total: number
  }
}

export async function getUnreadConversations(): Promise<UnreadConversationsSummary> {
  const response = await apiClient.get<{ data: UnreadConversationsSummary }>(
    '/me/unread-conversations',
  )
  return response.data.data
}

export async function markConversationRead(
  projectId: string,
  conversationId: string,
): Promise<void> {
  await apiClient.post(`/projects/${projectId}/conversations/${conversationId}/read`)
}

export async function renameConversation(
  projectId: string,
  conversationId: string,
  title: string,
): Promise<Conversation> {
  const response = await apiClient.patch<ApiResource<Conversation>>(
    `/projects/${projectId}/conversations/${conversationId}`,
    { title },
  )
  return response.data.data
}

export async function deleteConversation(projectId: string, conversationId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/conversations/${conversationId}`)
}

export async function sendMessage(
  projectId: string,
  conversationId: string,
  payload: { content: string; file?: File | null },
): Promise<Message> {
  const formData = new FormData()

  if (payload.content) {
    formData.append('content', payload.content)
  }

  if (payload.file) {
    formData.append('file', payload.file)
  }

  const response = await apiClient.post<ApiResource<Message>>(
    `/projects/${projectId}/conversations/${conversationId}/messages`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )

  return response.data.data
}

export async function interceptConversation(
  projectId: string,
  conversationId: string,
): Promise<Conversation> {
  const response = await apiClient.post<ApiResource<Conversation>>(
    `/projects/${projectId}/conversations/${conversationId}/intercept`,
  )
  return response.data.data
}

export async function releaseConversation(
  projectId: string,
  conversationId: string,
): Promise<Conversation> {
  const response = await apiClient.post<ApiResource<Conversation>>(
    `/projects/${projectId}/conversations/${conversationId}/release`,
  )
  return response.data.data
}

export async function sendOperatorMessage(
  projectId: string,
  conversationId: string,
  content: string,
  files: File[] = [],
): Promise<Message> {
  const formData = new FormData()

  if (content) {
    formData.append('content', content)
  }

  files.forEach((file) => formData.append('files[]', file))

  const response = await apiClient.post<ApiResource<Message>>(
    `/projects/${projectId}/conversations/${conversationId}/operator-message`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data.data
}

export async function closeConversation(
  projectId: string,
  conversationId: string,
): Promise<Conversation> {
  const response = await apiClient.post<ApiResource<Conversation>>(
    `/projects/${projectId}/conversations/${conversationId}/close`,
    {},
  )
  return response.data.data
}

export async function createManagerTopic(projectId: string, conversationId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/conversations/${conversationId}/manager-topic`, {})
}

export async function deleteManagerTopic(projectId: string, conversationId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/conversations/${conversationId}/manager-topic`)
}

export async function mirrorManagerTopicMessage(
  projectId: string,
  conversationId: string,
  text: string,
): Promise<void> {
  await apiClient.post(
    `/projects/${projectId}/conversations/${conversationId}/manager-topic/mirror`,
    { text },
  )
}

export async function listKnowledgeSources(projectId: string): Promise<KnowledgeSource[]> {
  const response = await apiClient.get<ApiCollection<KnowledgeSource>>(
    `/projects/${projectId}/knowledge/sources`,
  )
  return response.data.data
}

export async function listKnowledgeDocuments(
  projectId: string,
  search = '',
  page = 1,
): Promise<ApiPaginatedCollection<KnowledgeDocument>> {
  const response = await apiClient.get<ApiPaginatedCollection<KnowledgeDocument>>(
    `/projects/${projectId}/knowledge/documents`,
    {
      params: {
        ...(search.trim() ? { search } : {}),
        page,
        per_page: 20,
      },
    },
  )
  return response.data
}

export async function getKnowledgeDocument(
  projectId: string,
  documentId: string,
): Promise<KnowledgeDocument> {
  const response = await apiClient.get<ApiResource<KnowledgeDocument>>(
    `/projects/${projectId}/knowledge/documents/${documentId}`,
  )
  return response.data.data
}

export async function uploadKnowledgeFile(
  projectId: string,
  file: File,
  name?: string,
): Promise<KnowledgeSource> {
  const formData = new FormData()
  formData.append('file', file)

  if (name?.trim()) {
    formData.append('name', name.trim())
  }

  const response = await apiClient.post<ApiResource<KnowledgeSource>>(
    `/projects/${projectId}/knowledge/uploads`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )

  return response.data.data
}

export async function getKnowledgeStatus(projectId: string): Promise<KnowledgeStatus> {
  const response = await apiClient.get<ApiResource<KnowledgeStatus>>(
    `/projects/${projectId}/knowledge/status`,
  )
  return response.data.data
}

export interface ReindexKnowledgeOptions {
  force?: boolean
}

export async function reindexKnowledge(
  projectId: string,
  options: ReindexKnowledgeOptions = {},
): Promise<KnowledgeStatus> {
  const response = await apiClient.post<ApiResource<KnowledgeStatus>>(
    `/projects/${projectId}/knowledge/reindex`,
    options.force ? { force: true } : {},
  )
  return response.data.data
}

export async function deleteKnowledgeSource(projectId: string, sourceId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/knowledge/sources/${sourceId}`)
}

export async function deleteKnowledgeDocument(
  projectId: string,
  documentId: string,
): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/knowledge/documents/${documentId}`)
}

export interface UpdateKnowledgeDocumentPayload {
  title?: string | null
  content?: string | null
}

export async function updateKnowledgeDocument(
  projectId: string,
  documentId: string,
  payload: UpdateKnowledgeDocumentPayload,
): Promise<KnowledgeDocument> {
  const response = await apiClient.patch<ApiResource<KnowledgeDocument>>(
    `/projects/${projectId}/knowledge/documents/${documentId}`,
    payload,
  )
  return response.data.data
}

export interface CreateKnowledgeDocumentPayload {
  title: string
  content: string
}

export async function createKnowledgeDocument(
  projectId: string,
  payload: CreateKnowledgeDocumentPayload,
): Promise<KnowledgeDocument> {
  const response = await apiClient.post<ApiResource<KnowledgeDocument>>(
    `/projects/${projectId}/knowledge/documents`,
    payload,
  )
  return response.data.data
}

export async function listProjectMembers(projectId: string): Promise<ProjectMember[]> {
  const response = await apiClient.get<ApiCollection<ProjectMember>>(
    `/projects/${projectId}/members`,
  )
  return response.data.data
}

export async function addProjectMember(
  projectId: string,
  input: AddProjectMemberInput,
): Promise<ProjectMember> {
  const response = await apiClient.post<ApiResource<ProjectMember>>(
    `/projects/${projectId}/members`,
    input,
  )
  return response.data.data
}

export async function removeProjectMember(
  projectId: string,
  userId: number | string,
): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/members/${userId}`)
}

export interface ProjectInviteInput {
  email: string
}

export type ProjectInviteResult =
  | { outcome: 'assigned'; member: ProjectMember }
  | { outcome: 'invited'; invitation: OrganizationInvitation }

export async function inviteProjectMember(
  projectId: string,
  input: ProjectInviteInput,
): Promise<ProjectInviteResult> {
  const response = await apiClient.post<{ data: ProjectInviteResult }>(
    `/projects/${projectId}/members/invite`,
    input,
  )
  return response.data.data
}

export async function listProjectInvitations(projectId: string): Promise<OrganizationInvitation[]> {
  const response = await apiClient.get<ApiCollection<OrganizationInvitation>>(
    `/projects/${projectId}/members/invitations`,
  )
  return response.data.data
}

export async function revokeProjectInvitation(
  projectId: string,
  invitationId: string,
): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/members/invitations/${invitationId}`)
}

export async function getProjectPresets(): Promise<ProjectPreset[]> {
  const response = await apiClient.get<ApiCollection<ProjectPreset>>('/project-presets')
  return response.data.data
}

export async function listKbSuggestions(
  projectId: string,
  status: 'pending' | 'rejected' = 'pending',
): Promise<KbSuggestion[]> {
  const response = await apiClient.get<{ data: KbSuggestion[] }>(
    `/projects/${projectId}/kb-suggestions`,
    { params: { status } },
  )
  return response.data.data
}

export async function generateKbSuggestions(projectId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/kb-suggestions/generate`)
}

export async function approveKbSuggestion(projectId: string, suggestionId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/kb-suggestions/${suggestionId}/approve`)
}

export async function rejectKbSuggestion(projectId: string, suggestionId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/kb-suggestions/${suggestionId}`)
}

export interface ChunkLinkSide {
  title?: string | null
  snippet?: string | null
}

export interface ChunkLink {
  id: string
  status: 'pending' | 'confirmed' | 'rejected'
  score: number | null
  from: ChunkLinkSide | null
  to: ChunkLinkSide | null
}

export async function listChunkLinks(
  projectId: string,
  status: 'pending' | 'confirmed' = 'pending',
): Promise<ChunkLink[]> {
  const r = await apiClient.get<{ data: ChunkLink[] }>(
    `/projects/${projectId}/knowledge/chunk-links`,
    { params: { status } },
  )
  return r.data.data
}

export async function scanChunkLinks(projectId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/knowledge/chunk-links/scan`)
}

export async function confirmChunkLink(projectId: string, linkId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/knowledge/chunk-links/${linkId}/confirm`)
}

export async function rejectChunkLink(projectId: string, linkId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/knowledge/chunk-links/${linkId}/reject`)
}

export async function listProducts(
  projectId: string,
  params: { q?: string; page?: number; availability?: string } = {},
): Promise<ApiPaginatedCollection<Product>> {
  const response = await apiClient.get<ApiPaginatedCollection<Product>>(
    `/projects/${projectId}/catalog/products`,
    { params },
  )
  return response.data
}

export async function deleteProduct(projectId: string, productId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/catalog/products/${productId}`)
}

export async function getProduct(projectId: string, productId: string): Promise<Product> {
  const response = await apiClient.get<ApiResource<Product>>(
    `/projects/${projectId}/catalog/products/${productId}`,
  )
  return response.data.data
}

export interface UpdateProductPayload {
  title?: string
  description?: string
}

export async function updateProduct(
  projectId: string,
  productId: string,
  payload: UpdateProductPayload,
): Promise<Product> {
  const response = await apiClient.patch<ApiResource<Product>>(
    `/projects/${projectId}/catalog/products/${productId}`,
    payload,
  )
  return response.data.data
}

export async function bulkDeleteProducts(
  projectId: string,
  ids: string[],
): Promise<{ deleted: number }> {
  const response = await apiClient.post(`/projects/${projectId}/catalog/products/bulk-delete`, {
    ids,
  })
  return response.data
}

export async function purgeAllProducts(
  projectId: string,
): Promise<{ deleted_products: number; deleted_sync_runs: number }> {
  const response = await apiClient.delete(`/projects/${projectId}/catalog/products/purge`)
  return response.data
}

export async function uploadImportFile(projectId: string, file: File): Promise<CatalogSyncRun> {
  const form = new FormData()
  form.append('file', file)
  const response = await apiClient.post<ApiResource<CatalogSyncRun>>(
    `/projects/${projectId}/catalog/sync-runs/import`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data.data
}

export interface ImportPreview {
  columns: string[]
  examples: string[][]
  proposed_mapping: Record<string, string>
  is_yandex_market: boolean
}

export async function getImportPreview(projectId: string, runId: string): Promise<ImportPreview> {
  const response = await apiClient.get<ImportPreview>(
    `/projects/${projectId}/catalog/sync-runs/import/${runId}/preview`,
  )
  return response.data
}

export async function saveImportMapping(
  projectId: string,
  runId: string,
  mapping: Record<string, string>,
): Promise<CatalogSyncRun> {
  const response = await apiClient.post<ApiResource<CatalogSyncRun>>(
    `/projects/${projectId}/catalog/sync-runs/import/${runId}/mapping`,
    { mapping },
  )
  return response.data.data
}

export async function startImport(projectId: string, runId: string): Promise<CatalogSyncRun> {
  const response = await apiClient.post<ApiResource<CatalogSyncRun>>(
    `/projects/${projectId}/catalog/sync-runs/import/${runId}/run`,
  )
  return response.data.data
}

export async function getSyncRun(projectId: string, runId: string): Promise<CatalogSyncRun> {
  const response = await apiClient.get<ApiResource<CatalogSyncRun>>(
    `/projects/${projectId}/catalog/sync-runs/${runId}`,
  )
  return response.data.data
}

export async function listConsentVersions(projectId: string): Promise<ConsentVersion[]> {
  const r = await apiClient.get<{ data: ConsentVersion[] }>(
    `/projects/${projectId}/consent-versions`,
  )
  return r.data.data
}

export async function publishConsentVersion(
  projectId: string,
  text: string,
): Promise<ConsentVersion> {
  const r = await apiClient.post<{ data: ConsentVersion }>(
    `/projects/${projectId}/consent-versions`,
    { text },
  )
  return r.data.data
}

export async function listConsentAudit(projectId: string): Promise<ConsentAuditRow[]> {
  const r = await apiClient.get<{ data: ConsentAuditRow[] }>(`/projects/${projectId}/consents`)
  return r.data.data
}
